import React, { useState } from "react";
import { BaseDialog } from "../../dialog/BaseDialog";
import useInfiniteScrollQuery, {
  TInfiniteScrollContainerRef,
} from "../../../hooks/useInfiniteScroll";
import FbUsersService from "../../../services/FbUsersService";
import { UserRole, UsersSort } from "@growth-machine-llc/stridist-api-client";
import {
  List,
  ListItemAvatar,
  Avatar,
  ListItemText,
  ListItemButton,
  Button,
  CircularProgress,
  Box,
  Typography,
  TextField,
} from "@mui/material";
import AdminService from "../../../services/AdminService";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ADMIN_BRANDED_APPS_QUERY_KEY } from "./BrandedAppsTable";

interface AddBrandedAppDialogProps {
  open: boolean;
  onClose: () => void;
}

const PAGE_SIZE = 30;

export const AddBrandedAppDialog: React.FC<AddBrandedAppDialogProps> = ({
  open,
  onClose,
}) => {
  const [selectedCoachId, setSelectedCoachId] = useState<number | null>(null);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const queryClient = useQueryClient();

  // Debounce search input
  React.useEffect(() => {
    const handler = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(handler);
  }, [search]);

  const {
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    ref: sentinelRef,
  } = useInfiniteScrollQuery({
    queryKey: ["admin-coaches-list-infinite", debouncedSearch],
    queryFn: ({ pageParam = 1 }) =>
      FbUsersService.getAdminUsers(
        UserRole.COACH,
        pageParam as number,
        PAGE_SIZE,
        false,
        UsersSort.NAME_ASC,
        debouncedSearch,
      ),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce(
        (acc, page) => acc + (page.items?.length || 0),
        0,
      );
      return loaded < lastPage.totalCount ? allPages.length + 1 : undefined;
    },
    enabled: open,
  });

  const { mutate: createBrandedApp } = useMutation({
    mutationFn: AdminService.createBrandedApp,
    onSuccess: () => {
      onClose();
      queryClient.invalidateQueries({
        queryKey: [ADMIN_BRANDED_APPS_QUERY_KEY],
      });
    },
  });

  const coaches = data?.pages.flatMap((page) => page.items) || [];

  const handleSelect = (id: number) => setSelectedCoachId(id);
  const handleConfirm = () => {
    createBrandedApp({ coachId: selectedCoachId });
  };

  return (
    <BaseDialog
      open={open}
      onClose={onClose}
      title="Add Branded App"
      description="Select a coach to create a branded app for."
      maxWidth="xs"
    >
      <TextField
        fullWidth
        variant="outlined"
        placeholder="Search coaches"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        sx={{ mb: 2 }}
      />
      <Box sx={{ maxHeight: 400, overflow: "auto" }}>
        {isLoading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight={200}
          >
            <CircularProgress />
          </Box>
        ) : (
          <List>
            {coaches.map((coach) => {
              if (typeof coach.id !== "number") return null;
              return (
                <ListItemButton
                  key={coach.id}
                  selected={selectedCoachId === coach.id}
                  onClick={() => handleSelect(coach.id)}
                >
                  <ListItemAvatar>
                    <Avatar src={coach.photoUrl} />
                  </ListItemAvatar>
                  <ListItemText
                    primary={coach.displayName}
                    secondary={coach.email}
                  />
                </ListItemButton>
              );
            })}
            {!coaches.length && (
              <Box p={2} textAlign="center">
                <Typography>No coaches found.</Typography>
              </Box>
            )}
            {/* Sentinel for infinite scroll */}
            {hasNextPage && (
              <Box
                ref={sentinelRef as TInfiniteScrollContainerRef}
                display="flex"
                justifyContent="center"
                py={2}
              >
                <CircularProgress size={24} />
              </Box>
            )}
          </List>
        )}
      </Box>
      <Box display="flex" justifyContent="flex-end" mt={2}>
        <Button onClick={onClose} color="secondary" sx={{ mr: 1 }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          disabled={selectedCoachId === null}
          onClick={handleConfirm}
        >
          Confirm
        </Button>
      </Box>
    </BaseDialog>
  );
};
